import * as React from 'react';
import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from '../Icon/icons';
import { Icon } from '../Icon/Icon';
import {
  wrap as wrapCls,
  table as tableCls,
  caption as captionCls,
  th as thCls,
  sortButton as sortButtonCls,
  td as tdCls,
  empty as emptyCls,
} from './Table.css';

export type TableDensity = 'compact' | 'comfortable';
export type TableSortDirection = 'asc' | 'desc';

export interface TableColumn<Row> {
  key: string;
  header: React.ReactNode;
  /** Renderer for the row's cell. Defaults to `String(row[key])`. */
  render?: (row: Row, rowIndex: number) => React.ReactNode;
  /** Make the column sortable. The provided `compare` function determines order. */
  sortable?: boolean;
  /** Custom comparator. Default: localeCompare on the rendered cell value. */
  compare?: (a: Row, b: Row) => number;
  /** Cell horizontal alignment. */
  align?: 'left' | 'right' | 'center';
  /** CSS width / max-width. */
  width?: string;
  /** ARIA label override for the cell — useful for icon-only columns. */
  ariaLabel?: string;
}

export interface TableProps<Row> extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  rows: Row[];
  columns: TableColumn<Row>[];
  /** Stable id per row — used as the React key. */
  getRowId: (row: Row, index: number) => string | number;
  density?: TableDensity;
  /** Sticky header (requires the wrapper to have a constrained height). */
  stickyHeader?: boolean;
  /** Slot rendered when `rows` is empty. */
  empty?: React.ReactNode;
  /** ARIA-label / caption for screen readers. */
  caption?: string;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * Safe stringification for sort + render fallback. Objects/arrays return
 * empty string — the caller should provide `col.render` / `col.compare`
 * for non-primitive cells. This prevents the JS default
 * `[object Object]` leaking into the UI or breaking sort order.
 */
function safeString(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return String(v);
  return ''; // object / function / symbol — caller should render explicitly
}

function defaultCompare<Row>(col: TableColumn<Row>, a: Row, b: Row): number {
  const av = (a as Record<string, unknown>)[col.key];
  const bv = (b as Record<string, unknown>)[col.key];
  if (typeof av === 'number' && typeof bv === 'number') return av - bv;
  return safeString(av).localeCompare(safeString(bv));
}

/**
 * Table — typed, data-driven table with optional column sorting +
 * sticky header + custom cell renderers. Generic over the `Row` shape;
 * `columns` declare per-column header / render / sortable / compare /
 * align / width / aria-label. `getRowId` provides the stable id (and
 * React key) for each row.
 *
 * Sorting cycles asc → desc → unsorted on the active column. Each
 * column's `compare` overrides the default lexicographic / numeric
 * comparator. Sortable headers render an arrow indicator and announce
 * `aria-sort` to assistive tech. `empty` renders when `rows` is empty.
 */
export function Table<Row>({
  rows,
  columns,
  getRowId,
  density = 'comfortable',
  stickyHeader = false,
  empty,
  caption,
  ref,
  className,
  ...rest
}: TableProps<Row>) {
  const [sort, setSort] = useState<{ key: string; direction: TableSortDirection } | null>(null);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const cmp = col.compare ?? ((a: Row, b: Row) => defaultCompare(col, a, b));
    const copy = [...rows];
    copy.sort((a, b) => (sort.direction === 'asc' ? cmp(a, b) : -cmp(a, b)));
    return copy;
  }, [rows, sort, columns]);

  const cycle = (key: string) => {
    setSort((curr) => {
      if (!curr || curr.key !== key) return { key, direction: 'asc' };
      if (curr.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  };

  const wrapClassName = [wrapCls({ stickyHeader }), className].filter(Boolean).join(' ');

  if (rows.length === 0 && empty) {
    return (
      <div ref={ref} className={wrapClassName} {...rest}>
        <div className={emptyCls}>{empty}</div>
      </div>
    );
  }

  return (
    <div ref={ref} className={wrapClassName} {...rest}>
      <table className={tableCls}>
        {caption && <caption className={captionCls}>{caption}</caption>}
        <thead>
          <tr>
            {columns.map((col) => {
              const sortedHere = sort?.key === col.key;
              const ariaSort = sortedHere
                ? sort.direction === 'asc'
                  ? 'ascending'
                  : 'descending'
                : col.sortable
                  ? 'none'
                  : undefined;
              return (
                <th
                  key={col.key}
                  className={thCls({
                    align: col.align ?? 'left',
                    sticky: stickyHeader,
                    sortable: Boolean(col.sortable),
                  })}
                  aria-sort={ariaSort}
                  scope="col"
                  style={{ width: col.width, maxWidth: col.width }}
                >
                  {col.sortable ? (
                    <button type="button" className={sortButtonCls} onClick={() => cycle(col.key)}>
                      {col.header}
                      {sortedHere ? (
                        sort.direction === 'asc' ? (
                          <Icon as={ArrowUp} size="sm" />
                        ) : (
                          <Icon as={ArrowDown} size="sm" />
                        )
                      ) : (
                        <Icon as={ChevronsUpDown} size="sm" />
                      )}
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, i) => (
            <tr key={getRowId(row, i)}>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={tdCls({ align: col.align ?? 'left', density })}
                  aria-label={col.ariaLabel}
                  style={{ width: col.width, maxWidth: col.width }}
                >
                  {col.render
                    ? col.render(row, i)
                    : safeString((row as Record<string, unknown>)[col.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
Table.displayName = 'Table';
